<template>
  <!-- FollowRecommend.vueのonMenuHideに反応するのをstopPropagationで防ぐ -->
  <button :class="[$style.button, isLiked && $style.isLiked]" @click.stop="onClick">
    <HeartFillSvg v-if="isLiked" :class="$style.heartIcon" />
    <HeartActiveSvg v-else :class="$style.heartIcon" />
    {{ likedCount }}
  </button>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { likeItem, unlikeItem } from '@/js/lib/api/v1/item';
import { get as getLikeCount } from '@/js/lib/api/v2/public/items/like-count';
import { useItemStore } from '@/js/lib/store/item';
import { useUserStore } from '@/js/lib/store/user';
import { loading } from '@/js/lib/ui';
import { signinSignup } from '@/js/lib/ui/signin-signup';
import HeartActiveSvg from '@/svgs/vue/heart-active.svg';
import HeartFillSvg from '@/svgs/vue/heart-fill.svg';

const props = defineProps<{
  itemId: number;
}>();
const userStore = useUserStore();
const itemStore = useItemStore();
const isLoading = ref(false);
const isLiked = computed(() => itemStore.likedItemIds.includes(props.itemId));
const likedCount = ref(0);

const like = async () => {
  itemStore.addLiked(props.itemId);
  likedCount.value += 1;
  await likeItem({ itemId: props.itemId }).catch(() => {
    itemStore.removeLiked(props.itemId);
    likedCount.value -= 1;
  });

  window.dataLayer?.push({
    event: 'like',
    event_category: 'kpi',
    event_label: 'item',
  });
};
const unlike = async () => {
  itemStore.removeLiked(props.itemId);
  likedCount.value -= 1;
  await unlikeItem({ itemId: props.itemId }).catch(() => {
    itemStore.addLiked(props.itemId);
    likedCount.value += 1;
  });
};
const onClick = async () => {
  if (userStore.isAuthenticated) {
    if (isLoading.value) {
      return;
    }

    isLoading.value = true;
    await (isLiked.value ? unlike() : like());
    isLoading.value = false;
  } else {
    signinSignup({
      next: window.location.href,
      callback: async () => {
        await loading({
          asyncFunc: async () => {
            await like();
            window.location.reload();
          },
          text: 'ログインしています',
        });
      },
    });
  }
};

onMounted(async () => {
  const { data } = await getLikeCount({ itemId: props.itemId });
  likedCount.value = data.count;
  itemStore.checkLiked([props.itemId]);
});
</script>

<style module lang="scss">
.button {
  align-items: center;
  background-color: transparent;
  border-style: none;
  cursor: pointer;
  display: flex;
  font-size: 18px;
  gap: 4px;
  text-decoration: none;

  @media (--sp) {
    font-size: 16px;
  }

  &:hover {
    opacity: 0.8;
  }

  .heartIcon {
    fill: #4b4b4b;
    width: 48px;

    @media (--sp) {
      width: 44px;
    }
  }

  &.isLiked .heartIcon {
    fill: var(--action-button-heart-color);
  }
}
</style>
